import { CaretDown, Check } from "@phosphor-icons/react"
import {
  Select as AriaSelect,
  type Key,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  type ListBoxProps,
  Popover,
  type PopoverProps,
  SelectValue,
  type SelectValueProps,
} from "react-aria-components"
import { Button, type ButtonProps } from "../button/Button"
import { Icon } from "../icon"
import { Field, type FieldProps } from "./Field"

const Option = (props: ListBoxItemProps) => {
  const textValue = typeof props.children === "string" ? props.children : undefined
  return (
    <ListBoxItem
      textValue={textValue}
      {...props}
      className={({ isFocused, isSelected }) =>
        `tt-menu-item ${isFocused ? "focused" : ""} ${isSelected ? "selected" : ""}`
      }
    >
      {(renderProps) => (
        <div className="flex flex-row w-full items-center gap-2">
          <div className="flex-1">
            {typeof props.children === "function" ? props.children(renderProps) : props.children}
          </div>
          <Icon as={Check} className="tt-selected-icon text-fg-secondary" />
        </div>
      )}
    </ListBoxItem>
  )
}

type SelectFieldProps<T extends object> = Omit<FieldProps, "children"> & {
  children: ListBoxProps<T>["children"]
  isDisabled?: boolean
  isInvalid?: boolean
  items?: ListBoxProps<T>["items"]
  onSelectionChange?: (value: string) => void
  placeholder?: string
  selectedKey?: Key
  buttonProps?: Partial<ButtonProps>
  trigger?: (args: {
    selectValue: (props: SelectValueProps<T>) => React.ReactNode
  }) => React.ReactNode
  popoverProps?: PopoverProps
}

/**
 * A form field that allows users to select an option from a list. If you want a searchable select field, use `Combobox` component instead.
 *
 * ```tsx
 * import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
 *
 * <SelectField label="Favorite animal">
 *   <SelectField.Option>Aardvark</SelectField.Option>
 *   <SelectField.Option>Cat</SelectField.Option>
 *   <SelectField.Option>Dog</SelectField.Option>
 * </SelectField>
 * ```
 */

export const SelectField = <T extends object>({
  "aria-label": ariaLabel,
  children,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  items,
  label,
  // TODO: Expose prop to all field components that wrap a tt-core Field component
  labelIconSlot,
  placeholder = "Select an option",
  onSelectionChange,
  selectedKey,
  buttonProps,
  className,
  trigger,
  popoverProps,
}: SelectFieldProps<T>) => {
  // Convert label to string for aria-label if it's a string, otherwise use ariaLabel or undefined
  const accessibleLabel = ariaLabel ?? (typeof label === "string" ? label : undefined)
  return (
    <Field
      description={description}
      errorMessage={errorMessage}
      label={label}
      className={className}
      labelIconSlot={labelIconSlot}
    >
      <AriaSelect
        onSelectionChange={(val) => onSelectionChange?.(String(val))}
        selectedKey={selectedKey}
        aria-label={accessibleLabel}
      >
        {trigger ? (
          trigger({
            selectValue: SelectValue,
          })
        ) : (
          <Button
            isDisabled={isDisabled}
            color={isInvalid ? "danger" : undefined}
            {...buttonProps}
            className={`justify-between ${buttonProps?.className ?? ""}`}
          >
            <SelectValue>
              {({ defaultChildren, isPlaceholder }) => {
                return isPlaceholder ? placeholder : defaultChildren
              }}
            </SelectValue>
            <Icon as={CaretDown} />
          </Button>
        )}
        <Popover className="tt-popover" {...popoverProps}>
          <ListBox className="data-[focused]:outline-none" items={items}>
            {children}
          </ListBox>
        </Popover>
      </AriaSelect>
    </Field>
  )
}

SelectField.Option = Option
